import React, { useState } from "react";
import { useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/common";
import { MtaaWordmark } from "@/components/MtaaLogo";

const HERO = "https://images.pexels.com/photos/8117545/pexels-photo-8117545.jpeg?auto=compress&cs=tinysrgb&h=900&w=940";

function Shell({ children, title, subtitle }) {
  return (
    <div className="flex min-h-screen bg-background">
      <div className="relative hidden w-1/2 lg:block">
        <img src={HERO} alt="Mtaa community" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="mb-4"><MtaaWordmark className="[&_span]:text-white" /></div>
          <h2 className="font-display text-3xl font-extrabold leading-tight">Australia's Kenyan community marketplace.</h2>
          <p className="mt-2 text-white/80">Find trusted businesses, services, accommodation and community across Australia.</p>
        </div>
      </div>
      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm animate-fade-up">
          <Link to="/" className="mb-8 inline-flex lg:hidden">
            <MtaaWordmark />
          </Link>
          <h1 className="font-display text-2xl font-bold tracking-tight">{title}</h1>
          <p className="mb-6 mt-1 text-sm text-muted-foreground">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button variant="outline" className="w-full" onClick={go} data-testid="google-signin-btn">
      <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
      Continue with Google
    </Button>
  );
}

export function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.user, data.token);
      nav(loc.state?.from || (data.user.onboarded ? "/app" : "/onboarding"), { replace: true });
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Welcome back" subtitle="Sign in to your Mtaa account">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" /></div>
        <div>
          <div className="flex justify-between"><Label>Password</Label><Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-link">Forgot?</Link></div>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">{loading ? <Spinner className="text-white" /> : "Sign in"}</Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px flex-1 bg-border" />OR<div className="h-px flex-1 bg-border" /></div>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-muted-foreground">No account? <Link to="/register" className="font-semibold text-primary hover:underline" data-testid="to-register">Join Mtaa</Link></p>
    </Shell>
  );
}

export function Register() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      login(data.user, data.token);
      toast.success("Account created 🎉");
      nav("/onboarding", { replace: true });
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Welcome to Mtaa 🇰🇪🇦🇺" subtitle="Join the Kenyan-Australian community marketplace">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" /></div>
        <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email" /></div>
        <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} data-testid="register-password" /><p className="mt-1 text-xs text-muted-foreground">At least 8 characters</p></div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">{loading ? <Spinner className="text-white" /> : "Create account"}</Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px flex-1 bg-border" />OR<div className="h-px flex-1 bg-border" /></div>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-muted-foreground">Have an account? <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="to-login">Sign in</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Reset password" subtitle="We'll email you a secure reset link">
      {sent ? (
        <div className="rounded-xl border border-border bg-secondary/50 p-4 text-sm" data-testid="forgot-sent">
          If that email is registered, a reset link has been sent. Check your inbox.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" /></div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit">{loading ? <Spinner className="text-white" /> : "Send reset link"}</Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm"><Link to="/login" className="text-primary hover:underline">Back to sign in</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token"), password });
      toast.success("Password updated. Please sign in.");
      nav("/login", { replace: true });
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Choose a new password" subtitle="Enter a new password for your account">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>New password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="reset-password" /></div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit">{loading ? <Spinner className="text-white" /> : "Update password"}</Button>
      </form>
      <p className="mt-6 text-center text-sm"><Link to="/login" className="text-primary hover:underline">Back to sign in</Link></p>
    </Shell>
  );
}
